import { Vector3 } from '../../types/Vector3'
import { Quaternion } from '../../types/Quaternion'
import { LayoutInterface, AlignmentMode } from '../../field/FieldTypes'
import { FieldParameters } from '../../field/FieldParameters'
import { CurveUtils } from '../../../math/CurveUtils'
import { VectorUtils } from '../../../math/VectorUtils'
import { IndexTransforms } from '../../../math/IndexTransforms'

export class GridSphericalLayout implements LayoutInterface {
  // ==================== POSITION CALCULATION ====================
  
  /**
   * Calculate position for object at given index on sphere surface
   */
  public computePosition(index: number, totalCount: number, params: FieldParameters): Vector3 {
    // Convert index to grid coordinates
    const gridSize = this.calculateGridSize(totalCount)
    const coords = IndexTransforms.indexToGrid(index, gridSize)
    
    // Calculate sphere radius
    const radius = this.calculateSphereRadius(params, totalCount)
    
    // Get position on sphere using grid distribution
    const position = CurveUtils.getPointOnGridSphere(
      new Vector3(), // Center at origin
      radius,
      coords.row,
      coords.col,
      gridSize
    )
    
    return position
  }
  
  // ==================== ORIENTATION CALCULATION ====================
  
  /**
   * Calculate orientation for object at given index on sphere surface using 2D surface mathematics
   */
  public computeOrientation(index: number, totalCount: number, params: FieldParameters): Quaternion {
    const gridSize = this.calculateGridSize(totalCount)
    const coords = IndexTransforms.indexToGrid(index, gridSize)
    const position = this.computePosition(index, totalCount, params)
    const sphereCenter = new Vector3()
    
    // Convert grid coordinates to spherical parameters (0-1)
    const uParameter = coords.col / (gridSize - 1 || 1) // Longitude parameter (0-1)
    const vParameter = coords.row / (gridSize - 1 || 1) // Latitude parameter (0-1)
    
    // Use the improved 2D surface orientation calculation with existing enums
    const { right, up, forward } = CurveUtils.calculate2DSurfaceOrientation(
      'sphere', // Spherical surface
      position,
      null, // No plane normal for spheres
      sphereCenter, // Sphere center
      uParameter, // U coordinate (longitude, 0-1)
      vParameter, // V coordinate (latitude, 0-1)
      params.alignmentMode, // Already AlignmentMode enum
      params.upVectorMode // Already UpVectorMode enum
    )
    
    // Convert to quaternion using lookDir
    return VectorUtils.lookRotation(forward, up)
  }
  
  // ==================== TANGENT CALCULATION ====================
  
  /**
   * Calculate tangent vector (direction along sphere surface - longitude)
   */
  public computeTangent(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const gridSize = this.calculateGridSize(totalCount)
    const coords = IndexTransforms.indexToGrid(index, gridSize)
    const position = this.computePosition(index, totalCount, params)
    const center = new Vector3()
    
    return CurveUtils.getGridSphereTangent(center, position, coords.row, coords.col, gridSize)
  }
  
  // ==================== NORMAL CALCULATION ====================
  
  /**
   * Calculate normal vector (radial direction from sphere center)
   */
  public computeNormal(index: number, totalCount: number, params: FieldParameters): Vector3 {
    const position = this.computePosition(index, totalCount, params)
    const center = new Vector3()
    
    return CurveUtils.getSphereNormal(center, position)
  }
  
  // ==================== UTILITY METHODS ====================
  
  /**
   * Calculate grid size based on total count
   */
  private calculateGridSize(totalCount: number): number {
    // Create a roughly square grid that accommodates all objects
    return Math.ceil(Math.sqrt(totalCount))
  }
  
  /**
   * Calculate sphere radius based on parameters
   */
  private calculateSphereRadius(params: FieldParameters, totalCount: number): number {
    if (params.radius && params.radius > 0) {
      return params.radius
    }
    
    // Auto-calculate radius based on total count and desired spacing
    const spacing = params.cellSize + params.cellSpacing
    const surfaceArea = totalCount * spacing * spacing
    const radius = Math.sqrt(surfaceArea / (4 * Math.PI))
    
    return Math.max(radius, 5) // Minimum radius of 5
  }
  
  /**
   * Get main directional vector of the layout (sphere center to surface)
   */
  public getDirectionalVector(params: FieldParameters): Vector3 {
    // For sphere, the directional vector varies per position
    // Return a default upward direction
    return new Vector3(0, 1, 0)
  }
  
  /**
   * Get sphere bounds
   */
  public getSphereBounds(params: FieldParameters, totalCount: number): { 
    center: Vector3
    radius: number
  } {
    const radius = this.calculateSphereRadius(params, totalCount)
    
    return {
      center: new Vector3(0, 0, 0),
      radius: radius
    }
  }
  
  /**
   * Get grid dimensions
   */
  public getGridDimensions(totalCount: number): { width: number; height: number } {
    const gridSize = this.calculateGridSize(totalCount)
    return { width: gridSize, height: gridSize }
  }
  
  /**
   * Get all indices in a specific row
   */
  public getRowIndices(row: number, totalCount: number): number[] {
    const gridSize = this.calculateGridSize(totalCount)
    const indices: number[] = []
    
    for (let col = 0; col < gridSize; col++) {
      const index = row * gridSize + col
      if (index < totalCount) {
        indices.push(index)
      }
    }
    
    return indices
  }
  
  /**
   * Get all indices in a specific column
   */
  public getColumnIndices(col: number, totalCount: number): number[] {
    const gridSize = this.calculateGridSize(totalCount)
    const indices: number[] = []
    
    for (let row = 0; row < gridSize; row++) {
      const index = row * gridSize + col
      if (index < totalCount) {
        indices.push(index)
      }
    }
    
    return indices
  }
  
  /**
   * Check if index is at north or south pole
   */
  public isAtPole(index: number, totalCount: number): boolean {
    const gridSize = this.calculateGridSize(totalCount)
    const coords = IndexTransforms.indexToGrid(index, gridSize)
    
    return coords.row === 0 || coords.row === gridSize - 1
  }
  
  /**
   * Get spherical coordinates (latitude, longitude) for index
   */
  public getSphericalCoordinates(index: number, totalCount: number): {
    latitude: number
    longitude: number
  } {
    const gridSize = this.calculateGridSize(totalCount)
    const coords = IndexTransforms.indexToGrid(index, gridSize)
    
    const latitude = Math.PI * (coords.row / (gridSize - 1)) - Math.PI / 2
    const longitude = 2 * Math.PI * (coords.col / gridSize)
    
    return { latitude, longitude }
  }
  
  /**
   * Get distance between two points on sphere surface
   */
  public getSphericalDistance(
    index1: number, 
    index2: number, 
    totalCount: number, 
    params: FieldParameters
  ): number {
    const pos1 = this.computePosition(index1, totalCount, params)
    const pos2 = this.computePosition(index2, totalCount, params)
    const radius = this.calculateSphereRadius(params, totalCount)
    
    // Calculate angular distance
    const dot = pos1.dot(pos2) / (radius * radius)
    const angle = Math.acos(Math.max(-1, Math.min(1, dot)))
    
    return angle * radius
  }
  
  /**
   * Get neighbors of a grid position on sphere
   */
  public getNeighbors(index: number, totalCount: number): number[] {
    const gridSize = this.calculateGridSize(totalCount)
    const coords = IndexTransforms.indexToGrid(index, gridSize)
    const neighbors: number[] = []
    
    // Standard 4-connected neighbors
    const directions = [
      [-1, 0], [1, 0], [0, -1], [0, 1]
    ]
    
    for (const [deltaRow, deltaCol] of directions) {
      const newRow = coords.row + deltaRow
      const newCol = coords.col + deltaCol
      
      // Handle wrapping for longitude (columns)
      const wrappedCol = ((newCol % gridSize) + gridSize) % gridSize
      
      // Check latitude bounds (rows)
      if (newRow >= 0 && newRow < gridSize) {
        const neighborIndex = newRow * gridSize + wrappedCol
        if (neighborIndex < totalCount) {
          neighbors.push(neighborIndex)
        }
      }
    }
    
    return neighbors
  }
  
  /**
   * Get optimal sphere size for given parameters
   */
  public getOptimalSphereRadius(targetDensity: number, totalCount: number): number {
    const surfaceArea = totalCount / targetDensity
    return Math.sqrt(surfaceArea / (4 * Math.PI))
  }
} 